import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bot, Maximize2, Plus, X } from 'lucide-react'
import CioChat from './CioChat'
import { useCioChat } from './useCioChat'

/**
 * The AI CIO, reachable from anywhere: a launcher at the bottom right that opens a pane
 * down the right-hand side.
 *
 * Mounted once in the app layout, so the pane survives navigation — the point of docking
 * it is to ask about the page you are on without losing the page, and a pane that reset
 * on every link would be worse than the full page. The thread id is remembered across
 * reloads too.
 *
 * Open/width live in `useCioDock`, owned by the layout, because the layout has to shrink
 * the content to make room. A pane that covered the page would defeat the feature.
 *
 * It does not render on the AI CIO page itself, where it would be a second copy of what
 * already fills the screen.
 */

const CONV_KEY = 'plutus.cio.dock.conversation'

interface Props {
  open: boolean
  setOpen: (v: boolean | ((o: boolean) => boolean)) => void
  width: number
  startDrag: (e: React.MouseEvent) => void
}

export default function CioDock({ open, setOpen, width, startDrag }: Props) {
  const { pathname } = useLocation()
  const onOwnPage = pathname.startsWith('/ai-cio')
  const chat = useCioChat({ persistKey: CONV_KEY })

  // Ctrl/Cmd + J toggles, alongside the palette's Ctrl K. Escape closes, unless the
  // agent is mid-answer — losing a running question to a stray keypress is worse than
  // leaving the pane open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        setOpen(o => !o)
      } else if (e.key === 'Escape' && !chat.busy) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [chat.busy, setOpen])

  if (onOwnPage) return null

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="Ask the AI CIO  (Ctrl J)"
          aria-label="Open the AI CIO"
          className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 hover:shadow-xl">
          <Bot size={22} />
        </button>
      )}

      {open && (
        <aside
          style={{ width }}
          className="fixed bottom-0 right-0 top-0 z-40 flex flex-col border-l border-gray-200 bg-gray-50 shadow-xl">
          <div onMouseDown={startDrag} title="Drag to resize"
               className="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize hover:bg-blue-200/60" />

          <header className="flex flex-none items-center gap-2 border-b border-gray-200 bg-white px-3 py-2">
            <Bot size={16} className="flex-none text-blue-600" />
            <span className="flex-none text-sm font-semibold text-gray-900">AI CIO</span>
            {chat.status && (
              <span className="truncate text-[11px] text-gray-400">
                {chat.status.n_tools} tools · KB {chat.status.kb_version}
              </span>
            )}
            <div className="ml-auto flex flex-none items-center gap-1">
              <button onClick={chat.reset} title="New conversation"
                      className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                <Plus size={15} />
              </button>
              <Link to="/ai-cio" title="Open the full page"
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                <Maximize2 size={15} />
              </Link>
              <button onClick={() => setOpen(false)} title="Close  (Esc)"
                      className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                <X size={16} />
              </button>
            </div>
          </header>

          <div className="flex min-h-0 flex-1 flex-col p-3">
            <CioChat
              compact
              msgs={chat.msgs}
              busy={chat.busy}
              activeTools={chat.activeTools}
              status={chat.status}
              unavailable={chat.unavailable}
              onSend={chat.send}
              onStop={chat.stop}
            />
          </div>
        </aside>
      )}
    </>
  )
}
